import { CommandBar, ICommandBarItemProps, Icon, IconButton, Link, mergeStyles, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, Spinner, Stack } from "@fluentui/react"
import { useEffect, useRef, useState } from "react"
import { commandBarItemStyles } from "../../lib/helper"
import { getHTMLForQuoteDocumentGeneration } from "../../lib/quoteHtmlGenerator"
import { makeApiCall } from "../../services/apiService"
import { QuoteDataForOpportunityOutput } from "../../types/QuoteTypes"
import { PandaDocDocument, PandaDocRecipientOption, PandaDocsPanel, PandaDocStatusKey, PandaDocUpdateCode } from "./components/PandaDocPanel"

export function OpportunityQuote() {
	const [error, setError] = useState<string | null>(null)
	const [success, setSuccess] = useState<string | null>(null)
	const [loading, setLoading] = useState<boolean>(true)
	const [quoteData, setQuoteData] = useState<QuoteDataForOpportunityOutput>(undefined)
	const ref = useRef(null)
	const [exporting, setExporting] = useState<boolean>(false)
	const [pandaDocs, setPandaDocs] = useState<PandaDocDocument[]>([])
	const [recipientOptions, setRecipientOptions] = useState<PandaDocRecipientOption[]>([])
	const [loadingDocs, setLoadingDocs] = useState(false)
	const exportButtonContainerClass = mergeStyles({ display: "flex", alignItems: "center", marginBottom: 12 })
	const quoteHtmlContainerClass = mergeStyles({ width: "fit-content" })

	useEffect(() => {
		const fetchQuoteData = async () => {
			try {
				setLoading(true)
				const queryParameters = new URLSearchParams(window.top.location.search)
				const opportunityId = queryParameters.get("id")
				const quoteData = await makeApiCall(`getQuoteDataForOpportunity?opportunityId=${opportunityId}`, "GET")
				setQuoteData(quoteData)
			} catch (err) {
				console.error("Error from API:", err.message)
				setError(err.message || "An error occurred while fetching quote data.")
			} finally {
				setLoading(false)
			}
		}
		fetchQuoteData()
	}, [])

	async function refreshPandaDocs(showLoading = true) {
		if (showLoading) {
			setLoadingDocs(true)
		}
		setError(null)
		const queryParameters = new URLSearchParams(window.top.location.search)
		const opportunityId = queryParameters.get("id")

		try {
			const resp = await makeApiCall(`pandadoc/fetchPandaDocsMatchingOpportunity?opportunityId=${opportunityId}`, "POST", JSON.stringify({ matchOnTag: "quote" }))

			const mapped: PandaDocDocument[] = await Promise.all(
				(resp.results || []).map(async (d: any) => {
					const doc: PandaDocDocument = {
						id: d.id,
						name: d.name,
						status: d.status as PandaDocStatusKey,
						dateLabel: new Date(d.date_created).toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							year: "numeric"
						})
					}

					return doc
				})
			)

			setPandaDocs(mapped)
		} catch (e: any) {
			setError(e.message || "Failed to load PandaDoc documents.")
		} finally {
			if (showLoading) {
				setLoadingDocs(false)
			}
		}
	}

	async function refreshRecipientOptions() {
		setError(null)
		const queryParameters = new URLSearchParams(window.top.location.search)
		const opportunityId = queryParameters.get("id")
		if (!opportunityId) {
			setRecipientOptions([])
			return
		}

		try {
			const resp = await makeApiCall(`getOpportunitySigningRecipients?opportunityId=${opportunityId}`, "GET")
			const options: PandaDocRecipientOption[] = (resp?.recipients || []).map((recipient: any) => ({
				key: recipient.contactId || recipient.email,
				text: `${recipient.role}: ${recipient.displayName || recipient.email}`,
				email: recipient.email,
				displayName: recipient.displayName
			}))
			setRecipientOptions(options)
		} catch (e: any) {
			setError(e.message || "Failed to load recipient options.")
		}
	}

	const queryParameters = new URLSearchParams(window.top.location.search)
	const opportunityId = queryParameters.get("id")
	useEffect(() => {
		refreshPandaDocs() //Initial load
		void refreshRecipientOptions()
	}, [opportunityId])

	//Refresh PandaDocs every 30 seconds (silent refresh in background)
	useEffect(() => {
		const interval = setInterval(() => {
			refreshPandaDocs(false)
		}, 30000)

		return () => clearInterval(interval)
	}, [opportunityId])

	async function updatePandaDocStatus(documentId: string, updateCode: PandaDocUpdateCode) {
		const updated = await makeApiCall(`pandadoc/updatePandaDocStatus?documentId=${documentId}&status=${updateCode}`, "PATCH")

		const nextStatus = updated?.status as PandaDocStatusKey
		setPandaDocs(prev => prev.map(d => (d.id === documentId ? { ...d, status: nextStatus } : d)))
		await refreshPandaDocs()
	}

	async function generateSigningLink(documentId: string, recipientEmail: string, recipientDisplayName?: string): Promise<string> {
		const response = await makeApiCall(
			"pandadoc/generateEmbeddedSigningLinkId",
			"POST",
			JSON.stringify({
				pandaDocId: documentId,
				opportunityId,
				email: recipientEmail,
				displayName: recipientDisplayName
			})
		)

		if (!response?.signingUrl) {
			throw new Error("Failed to generate signing URL.")
		}

		return response.signingUrl as string
	}

	const getModelDrivenCurrentUserEmail = async (): Promise<string> => {
		const xrm = (window.top as any)?.Xrm
		const rawUserId = xrm?.Utility?.getGlobalContext?.()?.userSettings?.userId as string | undefined
		const userId = (rawUserId || "").replace(/[{}]/g, "")
		if (!xrm?.WebApi?.retrieveRecord || !userId) {
			throw new Error("Unable to resolve current CRM user context for BCC.")
		}

		const user = await xrm.WebApi.retrieveRecord("systemuser", userId, "?$select=internalemailaddress")
		const email = (user?.internalemailaddress as string | undefined)?.trim().toLowerCase()
		if (!email) {
			throw new Error("Current CRM user does not have an email address configured.")
		}

		return email
	}

	const getOpportunityAccountName = async (currentOpportunityId?: string | null): Promise<string> => {
		const xrm = (window.top as any)?.Xrm
		const normalizedOpportunityId = (currentOpportunityId || "").replace(/[{}]/g, "")
		if (!xrm?.WebApi?.retrieveRecord || !normalizedOpportunityId) {
			return ""
		}

		const opportunity = await xrm.WebApi.retrieveRecord("opportunity", normalizedOpportunityId, "?$select=_parentaccountid_value")
		const accountId = (opportunity?._parentaccountid_value as string | undefined)?.replace(/[{}]/g, "")
		if (!accountId) return ""

		const account = await xrm.WebApi.retrieveRecord("account", accountId, "?$select=name")
		return ((account?.name as string | undefined) || "").trim()
	}

	async function sendSigningLinkEmail(params: { documentId: string; companyName?: string; recipientEmail: string; recipientDisplayName?: string; signerLink: string }): Promise<void> {
		const bcc = await getModelDrivenCurrentUserEmail()
		const companyNameFromOpportunity = await getOpportunityAccountName(opportunityId)
		await makeApiCall(
			"pandadoc/sendEmbeddedSigningLinkEmail",
			"POST",
			JSON.stringify({
				pandaDocId: params.documentId,
				companyName: companyNameFromOpportunity || params.companyName || "",
				bcc,
				recipientName: params.recipientDisplayName || undefined,
				recipientEmail: params.recipientEmail,
				signerLink: params.signerLink
			})
		)
	}

	const getCommandBarFarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			{
				key: "exportImage",
				commandBarButtonAs: () => {
					return (
						<div className={exportButtonContainerClass}>
							<PrimaryButton
								iconProps={{ iconName: "Export" }}
								title="Export"
								ariaLabel="Export"
								styles={commandBarItemStyles}
								disabled={exporting}
								onClick={async () => {
									setExporting(true)
									setError(null)
									setSuccess(null)
									try {
										const html = getHTMLForQuoteDocumentGeneration(quoteData, 10)
										const result = await makeApiCall(
											`exportQuoteToPandaDocDocument`,
											"POST",
											JSON.stringify({
												opportunity: quoteData.Opportunity,
												broker: quoteData.BrokerData,
												html: html,
												aoaTable: quoteData.AOATable,
												raterContent: quoteData.RaterFileContent,
												ratingMethod: quoteData.Opportunity["cw_quotetype@OData.Community.Display.V1.FormattedValue"],
												plans: quoteData.NewPlans
											})
										)
										if (result && result.pandaDocId) {
											const pandaDocUrl = `https://app.pandadoc.com/a/#/documents/${result.pandaDocId}`
											setSuccess(
												`Document exported to PandaDoc successfully.\n\n` +
													`View your document: <a href="${pandaDocUrl}" target="_blank" rel="noopener noreferrer">${result.fileName}</a> or use the link in the dashboard to generate a signing link.`
											)
											await refreshPandaDocs()
										} else {
											setError("Document exported, but no PandaDoc link was generated. Please check the PandaDoc dashboard for your document.")
										}
									} catch (err) {
										setError(err.message || "Failed to export document to PandaDoc.")
									} finally {
										setExporting(false)
									}
								}}>
								<Stack horizontal tokens={{ childrenGap: 4 }}>
									{exporting && <Spinner />}
									<span>Export to PandaDoc</span>
								</Stack>
							</PrimaryButton>
						</div>
					)
				},
				buttonStyles: commandBarItemStyles
			}
		]
		return items
	}

	const quoteType = (window.top as any).Xrm.Page.data.entity.attributes.getByName("cw_quotetype").getValue() === 121640001 ? "composite" : "age rated"
	const year = (window.top as any).Xrm.Page.data.entity.attributes.getByName("cradf_effectivedate").getValue().getFullYear()
	const isRenewal = (window.top as any).Xrm.Page.data.entity.attributes.getByName("cw_isrenewal").getValue()
	return (
		<Stack tokens={{ childrenGap: 20 }} style={{ padding: 20 }}>
			{loading && <ProgressIndicator label={`Loading ${isRenewal ? "renewal" : "initial"} ${quoteType} quote data from ${year} rater files...`} />}

			{error && (
				<MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close" onDismiss={() => setError(null)}>
					{error}
				</MessageBar>
			)}
			{success && (
				<MessageBar messageBarType={MessageBarType.success} isMultiline dismissButtonAriaLabel="Close" onDismiss={() => setSuccess(null)}>
					<span dangerouslySetInnerHTML={{ __html: success }} />
				</MessageBar>
			)}

			{!loading && !error && quoteData && (
				<Stack tokens={{ childrenGap: 20 }}>
					<MessageBar messageBarType={MessageBarType.info} isMultiline={false} dismissButtonAriaLabel="Close" onDismiss={() => setError(null)}>
						The following quote is based on data from the following rater files:
						<ul>
							<li>
								<Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
									<Link target="_blank" href={"https://acmgmd.sharepoint.com/" + quoteData.NewPlans.RaterFile}>
										<Icon iconName="ExcelLogoInverse" style={{ marginRight: 8 }} />
										{year} Rater File
									</Link>
									<IconButton iconProps={{ iconName: "FolderOpen" }} href={"https://acmgmd.sharepoint.com/" + quoteData.NewPlans.RaterFolder} target="_blank" />
								</Stack>
							</li>
							{quoteData.CurrentPlans && (
								<li>
									<Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
										<Link target="_blank" href={"https://acmgmd.sharepoint.com/" + quoteData.CurrentPlans.RaterFile}>
											<Icon iconName="ExcelLogoInverse" style={{ marginRight: 8 }} />
											{year - 1} Rater File
										</Link>
										<IconButton iconProps={{ iconName: "FolderOpen" }} href={"https://acmgmd.sharepoint.com/" + quoteData.CurrentPlans.RaterFolder} target="_blank" />
									</Stack>
								</li>
							)}
						</ul>
					</MessageBar>
					<CommandBar
						styles={{
							root: {
								backgroundColor: "auto"
							}
						}}
						items={[]}
						farItems={getCommandBarFarItems()}
					/>
					<div ref={ref} className={quoteHtmlContainerClass} dangerouslySetInnerHTML={{ __html: getHTMLForQuoteDocumentGeneration(quoteData, 16) }} />
					<PandaDocsPanel
						headerTitle="ACMG"
						pandaDocs={pandaDocs}
						loading={loadingDocs}
						height={320}
						onRefresh={refreshPandaDocs}
						onUpdatePandaDocStatus={updatePandaDocStatus}
						recipientOptions={recipientOptions}
						onGenerateSigningLink={generateSigningLink}
						onSendSigningLinkEmail={sendSigningLinkEmail}
						setError={setError}
						setSuccess={setSuccess}
					/>
					{(window.top as any).CRM_DEBUG_LOCAL && <pre>{JSON.stringify(quoteData, null, 2)}</pre>}
				</Stack>
			)}
		</Stack>
	)
}
